import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  ProductionDailyReport,
  ProductionDailyReportAttachment,
  ProductionDailyReportPayload,
} from '../types/productionDailyReport';
import { SequentialCodeSuggestion } from '../types/sequentialCode';
import { DEFAULT_PAGE_SIZE, PageResponse } from '../types/page';

export async function fetchProductionDailyReports(params?: {
  keyword?: string;
  contractId?: number | null;
  page?: number;
  size?: number;
}): Promise<PageResponse<ProductionDailyReport>> {
  const { data } = await apiClient.get<PageResponse<ProductionDailyReport>>(
    '/production-daily-reports',
    {
      params: {
        ...(params?.keyword?.trim() ? { q: params.keyword.trim() } : {}),
        ...(params?.contractId != null ? { contractId: params.contractId } : {}),
        page: params?.page ?? 0,
        size: params?.size ?? DEFAULT_PAGE_SIZE,
      },
    }
  );
  return data;
}

export async function fetchNextProductionDailyReportNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/production-daily-reports/suggestions/report-no'
  );
  return data;
}

export async function fetchProductionDailyReport(
  reportId: number
): Promise<ProductionDailyReport> {
  const { data } = await apiClient.get<ProductionDailyReport>(
    `/production-daily-reports/${reportId}`
  );
  return data;
}

export async function createProductionDailyReport(
  payload: ProductionDailyReportPayload
): Promise<ProductionDailyReport> {
  const { data } = await apiClient.post<ProductionDailyReport>(
    '/production-daily-reports',
    payload
  );
  return data;
}

export async function updateProductionDailyReport(
  reportId: number,
  payload: ProductionDailyReportPayload
): Promise<ProductionDailyReport> {
  const { data } = await apiClient.patch<ProductionDailyReport>(
    `/production-daily-reports/${reportId}`,
    payload
  );
  return data;
}

export async function confirmProductionDailyReport(
  reportId: number
): Promise<ProductionDailyReport> {
  const { data } = await apiClient.post<ProductionDailyReport>(
    `/production-daily-reports/${reportId}/confirm`
  );
  return data;
}

export async function reopenProductionDailyReport(
  reportId: number
): Promise<ProductionDailyReport> {
  const { data } = await apiClient.post<ProductionDailyReport>(
    `/production-daily-reports/${reportId}/reopen`
  );
  return data;
}

export async function approveProductionDailyReport(
  reportId: number
): Promise<ProductionDailyReport> {
  const { data } = await apiClient.post<ProductionDailyReport>(
    `/production-daily-reports/${reportId}/approve`
  );
  return data;
}

export async function revokeProductionDailyReport(
  reportId: number
): Promise<ProductionDailyReport> {
  const { data } = await apiClient.post<ProductionDailyReport>(
    `/production-daily-reports/${reportId}/revoke`
  );
  return data;
}

export async function deleteProductionDailyReport(reportId: number): Promise<void> {
  await apiClient.delete(`/production-daily-reports/${reportId}`);
}

export async function uploadProductionDailyReportAttachments(
  reportId: number,
  files: File[]
): Promise<ProductionDailyReportAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<ProductionDailyReportAttachment[]>(
    `/production-daily-reports/${reportId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteProductionDailyReportAttachment(
  reportId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/production-daily-reports/${reportId}/attachments/${attachmentId}`);
}

export async function downloadProductionDailyReportAttachment(
  reportId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/production-daily-reports/${reportId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}


export async function downloadProductionDailyReportPdf(
  reportId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/production-daily-reports/${reportId}/pdf`,
    fileName || `생산일보_${reportId}.pdf`
  );
}
